import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Formik, Form } from 'formik';
import {
  useTranslator,
  FormikInput as Input,
  FormPrompt,
  useAppContext,
  useToast,
  formatCalories,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import MeasurementUnits from '../../Layout/MeasurementUnits';
import createValidationSchema from './validationSchema';
import TrackerFeedback from './TrackerFeedback';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Ingredient } from '../types';
import { useGlobalIngredients } from '../hooks/useGlobalIngredients';
import { useGlobalIngredientsEdit } from '../hooks/useGlobalIngredientsEdit';

const MULTIPLIER_STORAGE_KEY = 'tracker_multiplier';
const STORAGE_KEY_SHOULD_USE_DATA = 'tracker_should_use_data_from_session_storage';

interface TrackerEditMealPageProps {
  initialIngredients: Ingredient[];
  timestamp: number;
  mealName: string;
  requestId: string;
  mode: 'backward-navigation' | 'forward-navigation';
}

const TrackerEditMealPage: React.FC<TrackerEditMealPageProps> = ({
  initialIngredients,
  timestamp,
  mealName,
  requestId,
  mode,
}) => {
  const { t, language } = useTranslator();
  const decimalSeparator = language === 'pl-PL' ? ',' : '.';
  const navigate = useNavigate();
  const location = useLocation();
  const [isDirty, setIsDirty] = useState(false);
  const [localMealName, setLocalMealName] = useState(mealName);
  const { dietMealId } = useParams();

  const {
    images: { trashIcon, addIngredientIcon, editNeutralIcon, aiBadgeTiny, infoIcon },
  } = useAppContext();

  const { toastSuccess } = useToast();

  const isBarcode = window.location.search.includes('isBarcode=true');

  const [showModal, setShowModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const toggleAiModal = () => {
    setShowAiModal(!showAiModal);
  };

  // Reset isDirty when returning to this view (location changes)
  // This ensures FormPrompt works correctly on subsequent visits
  useEffect(() => {
    setIsDirty(false);
  }, [location.key]);

  // Initialize ingredients from sessionStorage or from initial state
  // Store ingredients for 1 portion in sessionStorage
  const { updateIngredients: updateIngredientsConfirm } = useGlobalIngredients(initialIngredients); // for confirm button
  const { ingredients, updateIngredients } = useGlobalIngredientsEdit(initialIngredients);

  // Use state instead of useMemo to make multiplier reactive
  const [multiplier, setMultiplier] = useState(() => {
    return parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');
  });

  // Listen for changes in sessionStorage for tracker_multiplier
  useEffect(() => {
    const handleStorageChange = () => {
      const newMultiplier = parseFloat(sessionStorage.getItem(MULTIPLIER_STORAGE_KEY) || '1');
      if (newMultiplier !== multiplier) {
        setMultiplier(newMultiplier);
      }
    };

    // Check for changes every 100ms (polling approach)
    // Note: storage event doesn't fire in the same tab, so we use polling
    const intervalId = setInterval(handleStorageChange, 100);

    return () => clearInterval(intervalId);
  }, [multiplier]);

  // Calculate formikValues based on ingredients from sessionStorage (stored for 1 portion)
  // Multiply by multiplier to display for current number of portions
  const formikValues = useMemo(() => {
    const values: { [key: string]: number | string | null } = {};

    values.mealName = localMealName;

    for (const ingredient of ingredients) {
      // Use measurements[1] if available, otherwise use measurements[0]

      let baseAmount = 0;
      let baseUnit = '';
      if (ingredient.measurements.length > 1) {
        baseAmount = ingredient.measurements?.[1]?.amount || 0;
        baseUnit = ingredient.measurements?.[1]?.unit || '';
      } else {
        baseAmount = ingredient.measurements?.[0]?.amount || 0;
        baseUnit = ingredient.measurements?.[0]?.unit || '';
      }

      console.log(multiplier, 'multiplier');

      console.log('baseAmount', baseAmount, multiplier);

      values[ingredient.id] = Math.round(baseAmount * multiplier * 100) / 100;

      // hack for barcode meal
      if (isBarcode) {
        if (
          (baseAmount === 100 &&
            (baseUnit === 'g' ||
              baseUnit === 'grams' ||
              baseUnit === 'ml' ||
              baseUnit === 'milliliters')) ||
          (baseAmount === 10 && baseUnit === 'cl') ||
          (baseAmount === 3 &&
            (baseUnit === 'oz' || baseUnit === 'fl_oz' || baseUnit === 'fluid ounces'))
        ) {
          values[ingredient.id] = null;
        }
      }
    }

    console.log('formikValues', values);

    return values;
  }, [ingredients, multiplier, localMealName]);

  const validationSchema = useMemo(() => createValidationSchema(ingredients), [ingredients]);

  const calculateUpdatedIngredients = (values: { [key: string]: number | string }) => {
    return ingredients.map((ingredient) => {
      const formValue = values[ingredient.id];
      if (
        formValue !== undefined &&
        typeof formValue === 'number' &&
        ingredient.measurements &&
        ingredient.measurements.length > 0
      ) {
        // Form value is already for 1 portion in sessionStorage, but user might have changed it
        // The displayed value is multiplied by multiplier, so we need to divide to get 1 portion value
        const amountPer1Portion = formValue / multiplier;

        // Get current stored amount (for 1 portion)
        const measurementIndex = ingredient.measurements.length > 1 ? 1 : 0;
        const currentStoredAmount = ingredient.measurements[measurementIndex].amount;
        const ratio = currentStoredAmount > 0 ? amountPer1Portion / currentStoredAmount : 1;

        if (ingredient.measurements.length > 1) {
          // Two measurements case
          return {
            ...ingredient,
            kcal: Math.round(ingredient.kcal * ratio),
            measurements: [
              {
                ...ingredient.measurements[0],
                amount: Math.round(ingredient.measurements[0].amount * ratio * 100) / 100,
              },
              {
                ...ingredient.measurements[1],
                amount: amountPer1Portion,
              },
            ],
            macro: ingredient.macro
              ? {
                  protein: {
                    grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                  },
                  fat: {
                    grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                  },
                  carbohydrates: {
                    grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                  },
                }
              : undefined,
          };
        } else {
          // Single measurement case
          return {
            ...ingredient,
            kcal: Math.round(ingredient.kcal * ratio),
            measurements: [
              {
                ...ingredient.measurements[0],
                amount: amountPer1Portion,
              },
            ],
            macro: ingredient.macro
              ? {
                  protein: {
                    grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                  },
                  fat: {
                    grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                  },
                  carbohydrates: {
                    grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                  },
                }
              : undefined,
          };
        }
      }
      return ingredient;
    });
  };

  const handleSubmit = (values: { [key: string]: number | string }) => {
    setIsDirty(false);
    // Set flag to use data from sessionStorage on next load
    sessionStorage.setItem(STORAGE_KEY_SHOULD_USE_DATA, 'true');
    // Save meal name to sessionStorage
    sessionStorage.setItem('tracker_meal_name', localMealName);
    // Values from form are for current portions, we need to divide by multiplier to store for 1 portion
    const updatedIngredients = calculateUpdatedIngredients(values);

    // Update ingredients in sessionStorage (stored for 1 portion)
    updateIngredientsConfirm(updatedIngredients);

    // navigate to add meal page
    setTimeout(() => {
      if (mode === 'forward-navigation') {
        navigate(`/tracker/describe-meal/meal-details/${dietMealId}`);
      } else {
        navigate(-1);
      }
    }, 0);
  };

  const handleAddIngredientBack = (deletedIngredient: any) => {
    // Use functional update to get the current state of ingredients
    updateIngredients((currentIngredients: Ingredient[]) => {
      // Check if ingredient is already in the list to avoid duplicates
      const alreadyExists = currentIngredients.some(
        (ingredient) => ingredient.id === deletedIngredient.id
      );
      if (alreadyExists) {
        return currentIngredients;
      }
      return [...currentIngredients, deletedIngredient];
    });
  };

  // Just mark form as dirty when values change
  const handleFormikChange = useCallback(() => {
    setIsDirty(true);
  }, []);

  const handleAddIngredient = (values: { [key: string]: number | string }) => {
    const updatedIngredients = calculateUpdatedIngredients(values);

    // Update ingredients in sessionStorage (stored for 1 portion)
    updateIngredients(updatedIngredients);

    // Set flag to use data from sessionStorage when returning
    sessionStorage.setItem('tracker_should_use_data_from_session_storage', 'true');

    navigate('add-ingredient');
  };

  const shortenUnit = (unit: string) => {
    if (unit === 'grams') {
      return 'g';
    } else if (unit === 'milliliters') {
      return 'ml';
    } else if (unit === 'oz') {
      return 'oz';
    } else if (unit === 'fl_oz') {
      return 'fl oz';
    } else {
      return unit;
    }
  };

  const handleSaveMealName = (values: any) => {
    setIsDirty(true);
    setLocalMealName(values.mealName);
    toggleModal();
  };

  const fakeSubmit = () => {
    // blur input
  };

  return (
    <Layout>
      <TitleBar showBackBtn={true}>{t('tracker.logMeal.ingredients.navbarTitle')}</TitleBar>

      <Formik
        onSubmit={fakeSubmit}
        initialValues={formikValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        key={location.key}
      >
        {({ isValid, values }) => {
          // Check if all ingredient fields are !== null
          const allFieldsValid = ingredients.every((ingredient) => {
            const value = values[ingredient.id];
            return value !== null && value !== undefined;
          });

          // Combine Formik's isValid with our custom null check
          const isFormValid = isValid && allFieldsValid;

          // Calculate display ingredients with updated measurements[0] based on current form values
          const displayIngredients = ingredients.map((ingredient) => {
            const formValue = values[ingredient.id];

            console.log(ingredient.name, ingredient.measurements.length > 1);

            if (
              formValue !== undefined &&
              typeof formValue === 'number' &&
              ingredient.measurements
              //ingredient.measurements.length > 1
            ) {
              console.log(ingredient, 'ingredient');

              // Calculate ratio based on form value change
              const amountPer1Portion = formValue / multiplier;
              let currentStoredAmount;

              if (ingredient.measurements.length > 1) {
                currentStoredAmount = ingredient.measurements[1].amount;
              } else {
                currentStoredAmount = ingredient.measurements[0].amount;
              }

              const ratio = currentStoredAmount > 0 ? amountPer1Portion / currentStoredAmount : 1;

              // Update measurements[0] for display only
              return {
                ...ingredient,
                measurements:
                  ingredient.measurements.length > 1
                    ? [
                        {
                          ...ingredient.measurements[0],
                          amount: Math.round(ingredient.measurements[0].amount * ratio * 100) / 100,
                        },
                        ingredient.measurements[1],
                      ]
                    : [ingredient.measurements[0]],

                kcal: Math.round(ingredient.kcal * ratio),
                macro: ingredient.macro
                  ? {
                      protein: {
                        grams: Math.round(ingredient.macro.protein.grams * ratio * 100) / 100,
                      },
                      fat: {
                        grams: Math.round(ingredient.macro.fat.grams * ratio * 100) / 100,
                      },
                      carbohydrates: {
                        grams: Math.round(ingredient.macro.carbohydrates.grams * ratio * 100) / 100,
                      },
                    }
                  : undefined,
              };
            }
            return ingredient;
          });

          const handleDeleteIngredientWithValues = async (id: string) => {
            setIsDirty(true);
            // First, calculate updated ingredients based on current form values
            const ingredientsBasedOnFormValues = calculateUpdatedIngredients(
              values as { [key: string]: number | string }
            );

            // Find the ingredient to delete (for undo functionality)
            const deletedIngredient = ingredientsBasedOnFormValues.find(
              (ingredient) => ingredient.id === id
            );

            // Remove the ingredient from the updated list
            const updatedIngredients = ingredientsBasedOnFormValues.filter(
              (ingredient) => ingredient.id !== id
            );

            // Update ingredients in sessionStorage
            await updateIngredients(updatedIngredients);

            toastSuccess(
              <p className="w-100">
                {t('tracker.logMeal.ingredients.deleteConfirmation.description')}

                <Button
                  color="link"
                  className="float-end"
                  onClick={() => handleAddIngredientBack(deletedIngredient)}
                >
                  {t('tracker.logMeal.ingredients.deleteConfirmation.button')}
                </Button>
              </p>
            );
          };

          return (
            <Form>
              <FormPrompt
                hasUnsavedChanges={isDirty}
                keyword={mode === 'backward-navigation' ? 'edit-meal' : dietMealId}
                mode={mode === 'backward-navigation' ? 'back' : 'exit'}
                key={`${dietMealId}-${mode}-${location.key}`}
              >
                {({
                  showModal,
                  toggleModal,
                  handleClick,
                }: {
                  showModal: boolean;
                  toggleModal: () => void;
                  handleClick: () => void;
                }) => (
                  <Modal isOpen={showModal} toggle={toggleModal} centered size="md">
                    <ModalHeader toggle={toggleModal} />
                    <ModalBody className="pt-0">
                      <h2 className="title-medium semi-bold text-center">
                        {t('tracker.logMeal.exitPopup.title')}
                      </h2>
                      <p className="text-center">{t('tracker.logMeal.exitPopup.description')}</p>

                      <Button
                        color="secondary"
                        className="w-100 mb-s"
                        outline
                        onClick={toggleModal}
                      >
                        {t('tracker.logMeal.exitPopup.backButton')}
                      </Button>

                      <Button color="secondary" className="w-100" outline onClick={handleClick}>
                        {t('tracker.logMeal.exitPopup.exitButton')}
                      </Button>
                    </ModalBody>
                  </Modal>
                )}
              </FormPrompt>
              <div className="page-template-tracker edit-meal-page">
                <Container className="py-md-4 full-height4">
                  <Row>
                    <Col xl={{ size: 10, offset: 1 }}>
                      <div className="page-container">
                        <div className="tracker-container">
                          <h2 className="title-large semi-bold mt-xxs mb-l">
                            <Button color="link" onClick={toggleModal} className="float-end p-0">
                              <img src={editNeutralIcon} alt="edit" className="ms-1" />
                            </Button>
                            {localMealName}
                          </h2>

                          {mode === 'forward-navigation' && (
                            <>
                              <div className="d-flex align-items-center mt-xl ">
                                <img
                                  src={aiBadgeTiny}
                                  alt=""
                                  className="me-s"
                                  style={{ width: '39px' }}
                                />
                                <p className="mb-0 description text-text-variant-color">
                                  {t('tracker.aiSignature')}
                                </p>
                                <img
                                  src={infoIcon}
                                  className="ms-s"
                                  style={{ width: '20px' }}
                                  onClick={toggleAiModal}
                                  alt=""
                                />
                              </div>

                              <hr className="mb-0" />
                            </>
                          )}

                          <ul className="tracker-ingredients-list-items px-0">
                            {displayIngredients.map((ingredient: Ingredient, index: number) => (
                              <li key={index} className="tracker-ingredient-item pt-s pb-0">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div
                                    className="d-flex flex-column justify-content-start mb-m"
                                    style={{ maxWidth: '60%' }}
                                  >
                                    <span className="small text-text-main-color semi-bold text-truncate-2-line">
                                      {ingredient.name}
                                      {ingredient.brand && ` (${ingredient.brand})`}
                                    </span>
                                  </div>
                                  <span className="text-text-main-color semi-bold d-flex">
                                    {ingredient.measurements &&
                                      ingredient.measurements.length > 1 && (
                                        <Input
                                          name={ingredient.id}
                                          type="number"
                                          unit={shortenUnit(ingredient.measurements[1].unit)}
                                          translateErrMsg={true}
                                          step={0.1}
                                          callbackOnChange={handleFormikChange}
                                        />
                                      )}

                                    {ingredient.measurements &&
                                      ingredient.measurements.length === 1 &&
                                      (ingredient.measurements[0].unit === 'milliliters' ||
                                        ingredient.measurements[0].unit === 'ml' ||
                                        ingredient.measurements[0].unit === 'cl' ||
                                        ingredient.measurements[0].unit === 'oz' ||
                                        ingredient.measurements[0].unit === 'fl oz' ||
                                        ingredient.measurements[0].unit === 'fl_oz' ||
                                        ingredient.measurements[0].unit === 'g' ||
                                        ingredient.measurements[0].unit === 'grams') && (
                                        <Input
                                          name={ingredient.id}
                                          type="number"
                                          unit={shortenUnit(ingredient.measurements[0].unit)}
                                          translateErrMsg={true}
                                          step={0.1}
                                          callbackOnChange={handleFormikChange}
                                        />
                                      )}
                                    <Button
                                      color="link"
                                      type="button"
                                      className="mb-m pe-0"
                                      onClick={() =>
                                        handleDeleteIngredientWithValues(ingredient.id)
                                      }
                                    >
                                      <img src={trashIcon} alt="delete" />
                                    </Button>
                                  </span>
                                </div>
                                <div className="mb-s">
                                  <span className="description text-text-variant-2-color d-flex justify-content-between align-items-center">
                                    <span>
                                      {formatCalories(ingredient.kcal * multiplier)} kcal
                                      <span className="d-inline-block ms-3">
                                        {t('summary.macros.proteinsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.protein?.grams ?? 0) *
                                              multiplier *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.fatsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.fat?.grams ?? 0) * multiplier * 10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                      <span className="d-inline-block ms-s">
                                        {t('summary.macros.carbsShort')}{' '}
                                        {(
                                          Math.round(
                                            (ingredient.macro?.carbohydrates?.grams ?? 0) *
                                              multiplier *
                                              10
                                          ) / 10
                                        )
                                          .toString()
                                          .replace('.', decimalSeparator)}
                                        g
                                      </span>
                                    </span>
                                    <span style={{ paddingRight: '28px' }}>
                                      {ingredient.measurements &&
                                        ingredient.measurements[0].unit !== 'milliliters' &&
                                        ingredient.measurements[0].unit !== 'cl' &&
                                        ingredient.measurements[0].unit !== 'ml' &&
                                        ingredient.measurements[0].unit !== 'oz' &&
                                        ingredient.measurements[0].unit !== 'fl oz' &&
                                        ingredient.measurements[0].unit !== 'fl_oz' &&
                                        ingredient.measurements[0].unit !== 'g' &&
                                        ingredient.measurements[0].unit !== 'grams' && (
                                          <>
                                            ≈
                                            <MeasurementUnits
                                              measurements={[ingredient.measurements[0]]}
                                              multiplier={multiplier}
                                              className="ms-1"
                                            />
                                          </>
                                        )}
                                    </span>
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>

                          <hr className="mt-0" />
                          <div className="d-flex justify-content-center">
                            <Button
                              color="link"
                              onClick={() =>
                                handleAddIngredient(values as { [key: string]: number | string })
                              }
                            >
                              <img src={addIngredientIcon} className="me-1" alt="add" />
                              <span className="semi-bold">
                                {t('tracker.logMeal.ingredients.addIngredientButton')}
                              </span>
                            </Button>
                          </div>

                          {mode === 'forward-navigation' && (
                            <TrackerFeedback requestId={requestId ?? ''} />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>

                <div className="sticky-footer">
                  <Container className="px-3">
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Button
                          type="button"
                          onClick={() => handleSubmit(values as { [key: string]: number | string })}
                          disabled={!isFormValid}
                          color="primary"
                          className="w-100"
                        >
                          {t('tracker.logMeal.ingredients.confirmButton')}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </div>

              <Modal
                className="from-bottom"
                isOpen={showModal}
                fullscreen={true}
                size="lg"
                toggle={toggleModal}
              >
                <div className="change-meal-name">
                  <Container>
                    <p className="small medium text-center">
                      {t('tracker.logMeal.mealName.edit.title')}
                    </p>
                    <Row>
                      <Col xs="12" md={{ size: 4, offset: 4 }}>
                        <Input
                          name="mealName"
                          label={t('tracker.logMeal.mealName.edit.inputLabel')}
                          type="text"
                          translateErrMsg
                        />
                        <Row>
                          <Col xs="6">
                            <Button
                              type="button"
                              color="link"
                              className="w-100"
                              onClick={toggleModal}
                            >
                              {t('tracker.logMeal.edit.cancelButton')}
                            </Button>
                          </Col>
                          <Col xs="6">
                            <Button
                              type="button"
                              color="primary"
                              className="w-100"
                              onClick={() => handleSaveMealName(values)}
                            >
                              {t('tracker.logMeal.mealName.edit.saveButton')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </Modal>
            </Form>
          );
        }}
      </Formik>

      <Modal isOpen={showAiModal} toggle={toggleAiModal} centered size="md">
        <ModalHeader toggle={toggleAiModal} />
        <ModalBody className="pt-0">
          <h2 className="title-medium semi-bold">{t('tracker.aiSignature.popup.title')}</h2>
          <p>{t('tracker.aiSignature.popup.description')}</p>

          <Button color="primary" className="w-100" onClick={toggleAiModal}>
            {t('tracker.aiSignature.popup.button')}
          </Button>
        </ModalBody>
      </Modal>
    </Layout>
  );
};

export default TrackerEditMealPage;
